/**
 * Standardized State Update Actions
 * Implements Smart Data-Flow Architecture principles
 * Provides consistent patterns for all entity operations
 * 
 * Replaces inconsistent update patterns across 103+ components:
 * - setStaffDetails(tempArr) → staffActions.set(tempArr)
 * - setCorporateData(data.CorporateList) → corporateActions.set(data.CorporateList)
 * - setDriverData(collectedDriverData) → driverActions.set(collectedDriverData)
 */

/**
 * Generic action creator factory
 * Creates standardized actions for any entity type
 */
export const createEntityActions = (entityType) => {
  const ENTITY_UPPER = entityType.toUpperCase();
  
  return {
    // Set entire entity list (replaces setStaffDetails, setCorporateData, etc.)
    set: (entities) => ({
      type: `${ENTITY_UPPER}/SET`,
      payload: Array.isArray(entities) ? entities : [],
      timestamp: Date.now()
    }),
    
    // Add single entity (for create operations)
    add: (entity) => ({
      type: `${ENTITY_UPPER}/ADD`,
      payload: entity,
      timestamp: Date.now()
    }),
    
    // Update existing entity (for edit operations)
    update: (id, updates) => ({
      type: `${ENTITY_UPPER}/UPDATE`,
      payload: { id, updates },
      timestamp: Date.now()
    }),
    
    // Remove entity (for delete operations)
    remove: (id) => ({
      type: `${ENTITY_UPPER}/REMOVE`,
      payload: id,
      timestamp: Date.now()
    }),
    
    // Set loading state
    setLoading: (loading) => ({
      type: `${ENTITY_UPPER}/SET_LOADING`,
      payload: loading
    }),
    
    // Set error state
    setError: (error) => ({
      type: `${ENTITY_UPPER}/SET_ERROR`,
      payload: error
    }),
    
    // Clear error state
    clearError: () => ({
      type: `${ENTITY_UPPER}/CLEAR_ERROR`
    }),
    
    // Clear entity data
    clear: () => ({
      type: `${ENTITY_UPPER}/CLEAR`,
      timestamp: Date.now()
    }),
    
    // Set filters (for search, pagination, etc.)
    setFilters: (filters) => ({
      type: `${ENTITY_UPPER}/SET_FILTERS`,
      payload: filters
    }),
    
    // Set selected items
    setSelected: (selectedItems) => ({
      type: `${ENTITY_UPPER}/SET_SELECTED`,
      payload: selectedItems
    }),
    
    // Batch operations for multiple entities
    batchUpdate: (updates) => ({
      type: `${ENTITY_UPPER}/BATCH_UPDATE`,
      payload: updates,
      timestamp: Date.now()
    }),
    
    // Refresh trigger (for forcing data reload)
    refresh: () => ({
      type: `${ENTITY_UPPER}/REFRESH`,
      timestamp: Date.now()
    })
  };
};

/**
 * Pre-created actions for all entities in the application
 * Standardizes state updates across the entire codebase
 */
export const staffActions = createEntityActions('STAFF');
export const corporateActions = createEntityActions('CORPORATE');
export const driverActions = createEntityActions('DRIVER');
export const routeActions = createEntityActions('ROUTE');
export const tripActions = createEntityActions('TRIP');
export const bookingActions = createEntityActions('BOOKING');
export const departmentActions = createEntityActions('DEPARTMENT');
export const settingsActions = createEntityActions('SETTINGS');
export const authActions = createEntityActions('AUTH');

/**
 * UI-specific actions for global application state
 */
export const uiActions = {
  setGlobalLoading: (loading) => ({
    type: 'UI/SET_GLOBAL_LOADING',
    payload: loading
  }),
  
  setGlobalError: (error) => ({
    type: 'UI/SET_GLOBAL_ERROR',
    payload: error
  }),
  
  clearGlobalError: () => ({
    type: 'UI/CLEAR_GLOBAL_ERROR'
  }),
  
  setNotification: (notification) => ({
    type: 'UI/SET_NOTIFICATION',
    payload: {
      id: Date.now(),
      timestamp: Date.now(),
      ...notification
    }
  }),
  
  removeNotification: (id) => ({
    type: 'UI/REMOVE_NOTIFICATION',
    payload: id
  }),
  
  setCurrentRoute: (route) => ({
    type: 'UI/SET_CURRENT_ROUTE',
    payload: route
  }),
  
  setGlobalFilters: (filters) => ({
    type: 'UI/SET_GLOBAL_FILTERS',
    payload: filters
  })
};

/**
 * Composite actions for complex operations involving multiple entities
 */
export const compositeActions = {
  // Load all application data (replaces scattered useEffect calls)
  loadAllData: () => ({
    type: 'COMPOSITE/LOAD_ALL_DATA',
    timestamp: Date.now()
  }),
  
  // Refresh specific entity group
  refreshEntityGroup: (entityTypes) => ({
    type: 'COMPOSITE/REFRESH_ENTITY_GROUP',
    payload: entityTypes,
    timestamp: Date.now()
  }),
  
  // Clear all data (for logout)
  clearAllData: () => ({
    type: 'COMPOSITE/CLEAR_ALL_DATA',
    timestamp: Date.now()
  }),
  
  // Sync related entities (when one changes, update related ones)
  syncRelatedEntities: (primaryEntity, relatedEntities) => ({
    type: 'COMPOSITE/SYNC_RELATED_ENTITIES',
    payload: { primaryEntity, relatedEntities },
    timestamp: Date.now()
  })
};

/**
 * Smart Data-Flow: Action creator utilities
 * Helper functions for creating consistent action patterns
 */
export const actionUtils = {
  /**
   * Create a standardized loading action
   */
  createLoadingAction: (entityType, isLoading) => ({
    type: `${entityType.toUpperCase()}/SET_LOADING`,
    payload: isLoading
  }),
  
  /**
   * Create a standardized error action
   */
  createErrorAction: (entityType, error) => ({
    type: `${entityType.toUpperCase()}/SET_ERROR`,
    payload: error,
    timestamp: Date.now()
  }),
  
  /**
   * Create a standardized success action
   */
  createSuccessAction: (entityType, data) => ({
    type: `${entityType.toUpperCase()}/SET_SUCCESS`,
    payload: data,
    timestamp: Date.now()
  }),
  
  /**
   * Create batch action for multiple operations
   */
  createBatchAction: (actions) => ({
    type: 'BATCH_ACTION',
    payload: actions,
    timestamp: Date.now()
  })
};

/**
 * Migration helpers for converting existing components
 * Makes it easier to convert from scattered useState to centralized actions
 */
export const migrationHelpers = {
  /**
   * Convert setState pattern to action dispatch
   */
  convertSetState: (entityType, setter) => {
    console.warn(`🚧 [MIGRATION] Converting ${entityType} setState to action dispatch`);
    const actions = createEntityActions(entityType);
    
    return {
      setData: (data) => actions.set(data),
      setLoading: (loading) => actions.setLoading(loading),
      setError: (error) => actions.setError(error),
      addItem: (item) => actions.add(item),
      updateItem: (id, updates) => actions.update(id, updates),
      removeItem: (id) => actions.remove(id)
    };
  },
  
  /**
   * Log migration progress
   */
  logMigration: (componentName, entityType) => {
    console.log(` [MIGRATION] ${componentName} migrated to use ${entityType}Actions`);
  }
};

// Export everything for easy importing
export default {
  // Entity actions
  staffActions,
  corporateActions,
  driverActions,
  routeActions,
  tripActions,
  bookingActions,
  departmentActions,
  settingsActions,
  authActions,
  
  // UI actions
  uiActions,
  
  // Composite actions
  compositeActions,
  
  // Utilities
  actionUtils,
  migrationHelpers,
  createEntityActions
};
